import { ApexOptions } from 'apexcharts';
import ReactApexChart from 'react-apexcharts';
import './RadarChart.css';
import React, { useEffect, useRef, useState } from 'react';

export const RadarChart = (props: any) => {
  const seriesRef = useRef<any[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [series, setSeries] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (props.data){
      setIsLoading(true);
      const timer = setTimeout(() => {
        const categories = Object.keys(props.data);
        const values = Object.values(props.data);

        const newSeries: any = [
          {
            data: values,
          },
        ];

        seriesRef.current = newSeries;
        setCategories(categories);
        setSeries(newSeries);
        setIsLoading(false);
      }, 100);
      return () => {
        clearTimeout(timer);
        // Clean up any existing tooltips
        removeTooltip();
      };
    }
  }, [props.data]);

  // Clean up tooltips on component unmount and add global listeners
  useEffect(() => {
    const handleScroll = () => removeTooltip();
    const handleClick = () => removeTooltip();
    const handleResize = () => removeTooltip();

    window.addEventListener('scroll', handleScroll);
    window.addEventListener('click', handleClick);
    window.addEventListener('resize', handleResize);

    return () => {
      removeTooltip();
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('click', handleClick);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  if (!props.data || isLoading) {
    return (
      <div 
        style={{ 
          height: 600, 
          width: 600,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#f8f9fa',
          borderRadius: '8px'
        }}
      >
        <div style={{ color: '#6b7280', fontSize: '14px' }}>Loading...</div>
      </div>
    );
  }

  function showTooltip(event: any, chartContext: any, config: any) {
    var seriesIndex = config.seriesIndex;
    
    // Remove existing tooltip first
    removeTooltip();

    if (seriesIndex !== -1) {
      const data = seriesRef.current[0].data[config.dataPointIndex];
      if (data === undefined) return;

      // Get mouse position relative to the viewport
      const mouseX = event.clientX || event.pageX;
      const mouseY = event.clientY || event.pageY;

      let div = document.createElement('div');
      div.id = 'apex-radar-custom-tooltip';
      div.className = 'apex-radar-custom-tooltip';
      div.style.position = 'fixed';
      div.style.pointerEvents = 'none';
      div.style.zIndex = '9999';
      div.style.left = mouseX + 10 + 'px';
      div.style.top = mouseY - 40 + 'px';
      div.style.background = 'rgba(0, 0, 0, 0.9)';
      div.style.color = 'white';
      div.style.padding = '8px 12px';
      div.style.borderRadius = '8px';
      div.style.fontSize = '12px';
      div.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.3)';
      div.style.transition = 'opacity 0.2s ease-in-out';
      div.style.opacity = '0';
      div.style.whiteSpace = 'nowrap';
      
      if (props.id === 'wind-direction-radar-chart') {
        div.innerHTML = data + ' avg. number of birds';
      } else {
        div.innerHTML = data.toFixed(2) + '% of the birds fly toward ' + config.config.xaxis.categories[config.dataPointIndex];
      }
      
      // Append to body instead of chart container
      document.body.appendChild(div);
      
      // Fade in the tooltip
      requestAnimationFrame(() => {
        div.style.opacity = '1';
      });
    }
  }
  function removeTooltip() {
    // Remove all existing radar tooltips to prevent multiple tooltips
    const existingTooltips = document.querySelectorAll('#apex-radar-custom-tooltip, .apex-radar-custom-tooltip');
    existingTooltips.forEach(tooltip => {
      if (tooltip instanceof HTMLElement) {
        tooltip.style.opacity = '0';
        setTimeout(() => {
          if (tooltip && tooltip.parentNode) {
            tooltip.remove();
          }
        }, 200);
      }
    });
  }

  const options: ApexOptions = {
    chart: {
      type: 'radar',
      toolbar: {
        show: false,
      },
      animations: {
        enabled: true,
        easing: 'easeinout',
        speed: 300,
        dynamicAnimation: {
          enabled: false,
        },
      },
      events: {
        mouseMove: (event: any, chartContext: any, config: any) => {
          showTooltip(event, chartContext, config);
        },
        mouseLeave: (event: any, chartContext: any, config: any) => {
          removeTooltip();
        },
      },
    },

    xaxis: {
      categories: categories,
    },
    yaxis: props.yaxisOptions,
    fill: {
      colors: ['#350B73'],
      opacity: 0.2,
    },
    stroke: {
      show: true,
      width: 0.5,
      colors: ['#350B73'],
      dashArray: 0,
    },
    markers: {
      size: 2,
      colors: ['#350B73'],
      hover: {
        size: 5,
      },
    },
    plotOptions: {
      radar: {
        polygons: {
          strokeColors: '#B6B6B6',
          connectorColors: '#B6B6B6',
          fill: {
            colors: ['#fff'],
          },
        },
      },
    },
    tooltip: {
      enabled: true,
      x: {
        show: true,
      },
      intersect: true,
      shared: false,
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        return '';
      },
    },
  };
  return (
    <div id={props.id || "radar"} className="radar">
      <ReactApexChart
        options={options}
        series={series}
        type="radar"
        height={600}
        width={600}
      />
    </div>
  );
};
